import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../contexts/AuthContext';
import { useStoreState } from 'easy-peasy';
import { APP_VERSION } from '../../appVersion';
import './Dashboard.scss';
import ytmLogo from '../../assets/img/brand/ytm.jpg';
import defaultAvatar from '../../assets/images/boss-1.png';

export default function DashboardHeader() {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { myUser, logout } = useAuthContext();
  const account = useStoreState((state) => state.account?.account);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleLogout = () => {
    setDropdownOpen(false);
    logout();
  };

  const getLanguageName = (langCode) => {
    const languages = {
      'fi': 'Suomi',
      'en': 'English',
      'sv': 'Svenska'
    };
    return languages[langCode] || langCode;
  };

  const getRoleName = (role) => {
    // Same keys as Users page filters / UserList (user_role_display_*)
    const id = [1, 2, 3, 4, 5].includes(Number(role)) ? Number(role) : 4;
    return t(`user_role_display_${id}`);
  };

  const profilePicture = myUser?.profile_picture || defaultAvatar;

  return (
    <div className="dashboard-header p-3 d-flex align-items-center justify-content-between">
      <img src={ytmLogo} alt="YTM Logo" className="dashboard-logo" style={{ height: 40 }} />
      <div className="d-flex align-items-center position-relative" ref={dropdownRef}>
        <img
          src={profilePicture}
          alt="Profile"
          className="dashboard-profile rounded-circle"
          style={{ width: 40, height: 40, objectFit: 'cover', cursor: 'pointer' }}
          onClick={toggleDropdown}
        />
        
        {dropdownOpen && (
          <div className="profile-dropdown">
            <div className="profile-dropdown-header">
              <img
                src={profilePicture}
                alt="Profile"
                className="rounded-circle"
                style={{ width: 50, height: 50, objectFit: 'cover' }}
              />
            </div>
            <div className="profile-dropdown-body">
              {account?.name && (
                <div className="profile-info-item">
                  <strong>{t('Company')}:</strong>
                  <span>{account.name}</span>
                </div>
              )}
              {(myUser?.first_name || myUser?.last_name) && (
                <div className="profile-info-item">
                  <strong>{t('Nimi')}:</strong>
                  <span>{`${myUser.first_name || ''} ${myUser.last_name || ''}`.trim()}</span>
                </div>
              )}
              <div className="profile-info-item">
                <strong>{t('phone_input')}:</strong>
                <span>{myUser?.phone || '-'}</span>
              </div>
              {myUser?.language && (
                <div className="profile-info-item">
                  <strong>{t('language')}:</strong>
                  <span>{getLanguageName(myUser.language)}</span>
                </div>
              )}
              {myUser?.username && (
                <div className="profile-info-item">
                  <strong>{t('Sähköposti')}:</strong>
                  <span>{myUser.username}</span>
                </div>
              )}
              {myUser?.role && (
                <div className="profile-info-item">
                  <strong>{t('Rooli')}:</strong>
                  <span>{getRoleName(myUser.role)}</span>
                </div>
              )}
              {APP_VERSION && (
                <div className="profile-info-item">
                  <strong>{t('web_app_version', 'Sovellusversio')}</strong>
                  <span>{APP_VERSION}</span>
                </div>
              )}
            </div>
            <div className="profile-dropdown-footer">
              <button 
                className="btn btn-danger w-100"
                onClick={handleLogout}
              >
                {t('dashboard_logout')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 